import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { Tooltip, MenuItem, IconButton, Menu, Typography, Box } from '@mui/material';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';

import { ScheduleServiceQuery } from '../../../../services/query';
import { useScheduleService } from '../../context/ScheduleServiceContext';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ScheduleServiceCommand } from '../../../../services/command';
import TimeHelper from '../../../../utils/time';
import { SL } from './scheduleList.classes';

const getResultIcons = (isEnabled: boolean) =>
    isEnabled ? (
        <Tooltip title="Success">
            <CheckCircleIcon className={SL.statusIconSuccess} sx={{ fontSize: '24px' }} color="success" />
        </Tooltip>
    ) : (
        <Tooltip title="Failed">
            <CancelIcon className={SL.statusIconError} sx={{ fontSize: '24px' }} color="error" />
        </Tooltip>
    );

const getTooltipValue = (params: any) => (
    <Tooltip className={SL.cellTooltip} title={params.value || ''} arrow>
        <Typography
            className={SL.cellTooltipText}
            noWrap
            sx={{ textOverflow: 'ellipsis', overflow: 'hidden', width: '100%' }}
        >
            {params.value || '-'}
        </Typography>
    </Tooltip>
);

export const ScheduleList = () => {
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [gridData, setGridData] = useState<any>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    const { setSelectedJob, setIsLoading, setIsEditing, userScheduleGroups, setUserScheduleGroups } =
        useScheduleService();
    const { t } = useTranslation();

    const getGridData = (pageNumber: number, pageSize: number, groups: any) => {
        setIsLoading(true);

        ScheduleServiceQuery.GetServiceList({
            take: pageSize,
            page: pageNumber + 1,
            OwnerTeamIds: groups,
        })
            .then((res: any) => {
                setGridData(res.data);
                setTotalCount(res.count);
                setIsLoading(false);
            })
            .catch((res: any) => {
                Swal.fire(t(res.error) || t('general.failed'), '', 'error');
                setIsLoading(false);
            });
    };

    const handleEditSchedule = (scheduleToEdit: any) => {
        setSelectedJob(scheduleToEdit);
        setIsEditing(true);
    };

    const handleDeleteSchedule = (scheduleToDelete: any) => {
        setIsLoading(true);
        ScheduleServiceCommand.DeleteSchedule({
            jobId: scheduleToDelete.jobId,
        })
            .then((res: any) => {
                if (res) {
                    Swal.fire({
                        position: 'center',
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    getGridData(pageNumber, pageSize, userScheduleGroups);
                }
            })
            .catch(() => {
                Swal.fire(t('general.success'), '', 'error');
                setIsLoading(false);
            });
    };

    const columnData: any = [
        {
            field: 'jobId',
            headerName: t('scheduleService.jobId'),
            renderCell: (params: any) => (
                <Tooltip className={SL.cellJobIdTooltip} title={params.value || ''} arrow>
                    <Typography
                        className={SL.cellJobIdText}
                        onClick={() => setSelectedJob(params?.row)}
                        noWrap
                        sx={{ textOverflow: 'ellipsis', overflow: 'hidden', width: '100%', cursor: 'pointer' }}
                        data-job-id={params?.row?.jobId}
                    >
                        {params.value || '-'}
                    </Typography>
                </Tooltip>
            ),
        },
        { field: 'jobName', headerName: t('scheduleService.jobName'), minWidth: 200, flex: 1 },
        {
            field: 'url',
            headerName: t('scheduleService.scheduleUrl'),
            flex: 1,
            renderCell: (params: any) => getTooltipValue(params),
        },
        {
            field: 'timeZoneId',
            headerName: t('scheduleService.timeZone'),
        },
        {
            field: 'lastExecutionTime',
            headerName: t('scheduleService.lastExecutionTime'),
            renderCell: (params: any) => getTooltipValue({ value: TimeHelper.formatDateHourSecond(params?.value) }),
            flex: 1,
        },
        {
            field: 'lastExecutionStatus',
            headerName: t('scheduleService.lastExecutionStatus'),
            renderCell: (params: any) => getTooltipValue(params),
            flex: 1,
        },
        {
            field: 'isEnabled',
            headerName: t('scheduleService.isEnabled'),
            renderCell: (params: any) => getResultIcons(params.value),
        },
        {
            field: 'cronDescription',
            headerName: t('scheduleService.cronDescription'),
            renderCell: (params: any) => getTooltipValue(params),
        },
        {
            field: 'ownerTeamName',
            headerName: t('scheduleService.ownerTeamName'),
            renderCell: (params: any) => getTooltipValue(params),
        },
        {
            field: 'detay',
            headerName: t('general.detail'),
            align: 'center',
            headerAlign: 'center',
            renderCell: (cellData: any) => {
                return (
                    <PopupState variant="popover" popupId="demo-popup-menu">
                        {(popupState: any) => (
                            <>
                                <IconButton
                                    className={SL.rowMenuButton}
                                    {...bindTrigger(popupState)}
                                    data-job-id={cellData?.row?.jobId}
                                >
                                    <MoreVertIcon className={['svg_icons', SL.rowMenuIcon].filter(Boolean).join(' ')} />
                                </IconButton>
                                <Menu {...bindMenu(popupState)} slotProps={{ paper: { className: SL.rowMenuPaper } }}>
                                    <MenuItem
                                        className={SL.rowMenuItemDetail}
                                        onClick={() => setSelectedJob(cellData?.row)}
                                    >
                                        {t('general.detail')}
                                    </MenuItem>
                                    <MenuItem
                                        className={SL.rowMenuItemEdit}
                                        onClick={() => handleEditSchedule(cellData?.row)}
                                    >
                                        {t('general.edit')}
                                    </MenuItem>
                                    <MenuItem
                                        className={SL.rowMenuItemDelete}
                                        onClick={() => handleDeleteSchedule(cellData?.row)}
                                    >
                                        {t('general.delete')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </PopupState>
                );
            },
        },
    ];

    useEffect(() => {
        ScheduleServiceQuery.GetUserScheduleGroups()
            .then((res: any) => {
                if (res) {
                    setUserScheduleGroups(res.data?.groupIds);
                }
            })
            .catch(() => Swal.fire(t('general.failed'), '', 'error'));
    }, []);

    useEffect(() => {
        if (userScheduleGroups?.length > 0) {
            getGridData(pageNumber, pageSize, userScheduleGroups);
        }
    }, [pageNumber, pageSize, userScheduleGroups]);

    return (
        <Box className={SL.root} sx={{ width: '100%' }}>
            <div className={SL.gridWrap}>
                <SCUICustomDataGrid
                    onPageSizeChange={(i: number) => {
                        setPageSize(i);
                        setPageNumber(0);
                    }}
                    pageSize={pageSize}
                    paginationModeChange
                    onPageChange={(i: number) => {
                        setPageNumber(i);
                    }}
                    page={pageNumber}
                    rows={gridData || []}
                    columns={columnData}
                    rowCount={totalCount}
                />
            </div>
        </Box>
    );
};
